import React, { useState, useEffect } from 'react';
import classnames from 'classnames';

// Components
import WpIcon from '../icon/WpIcon';
import { WpTooltip, WpTooltipTrigger, WpTooltipContent } from '../tooltip';

// Styles
import './WpListSelect.scss';

export interface StrictOption {
  /** Machine value of the option; this is the value passed to `onChange` */
  value: string | number;
  /** Human-readable text for the option */
  label: string;
  /** optional tooltip to appear on hover */
  tooltip?: string;
  /** Option will be visible, but not selectable */
  disabled?: boolean;
  /** Allow other data for custom option */
  [x: string]: any;
}

interface StrictIcon {
  name: string;
  prefix: string;
}

export type ListSelectOption = string | StrictOption;

export type IconType = string | StrictIcon;

type Props = {
  id: string;
  /** List of options or option groups to choose from */
  options?: ListSelectOption[];
  direction?: 'horizontal' | 'column';
  onChange?(selected: ListSelectOption[]): void;
  max?: number;
  iconNotSelected?: IconType;
  iconSelected?: IconType;
  showIcon?: boolean;
  selectedOptions?: ListSelectOption[];
  qaTest?: string;
  components?: {
    optionInner?: Function;
  };
  disableOnMax?: boolean;
  disabled?: boolean;
};

const WpListSelect: React.FC<Props> = ({
  id,
  options = [],
  direction = 'column',
  onChange,
  max,
  iconNotSelected = 'plus-circle',
  iconSelected = 'check-circle',
  showIcon = true,
  selectedOptions = [],
  qaTest,
  components,
  disableOnMax = true,
  disabled = false,
}: Props) => {
  const [selected, setSelected] = useState([]);
  const baseClassName = 'WpListSelect';

  function isOptionSelected(option: StrictOption) {
    return selected.filter(item => item.value === option.value).length > 0;
  }

  function handleChange(option: StrictOption) {
    if (option.disabled) return null;

    let newSelected = [];
    if (isOptionSelected(option)) {
      newSelected = selected.filter(item => item.value !== option.value);
    } else {
      newSelected = [...selected, { ...option }];
      if (!disableOnMax && max && selected.length === max) {
        newSelected.shift();
      }
    }
    setSelected(newSelected);

    if (onChange) {
      onChange(newSelected);
    }
  }

  function normalizeStringOption(option: string): StrictOption {
    return {
      label: option,
      value: option,
      disabled: false,
    };
  }

  /**
   * Converts a string option (and each string option in a Group) into
   * an Option object.
   */

  function normalizeOption(option: ListSelectOption): StrictOption {
    if (typeof option === 'string') {
      return normalizeStringOption(option);
    } else {
      return option;
    }
  }

  function normalizeIcon(icon: IconType): StrictIcon {
    if (typeof icon === 'string') {
      return { name: icon, prefix: 'fal' };
    } else {
      return icon;
    }
  }

  const normalizedOptions = options.map(normalizeOption);
  const normalizedNotSelectedIcon = normalizeIcon(iconNotSelected);
  const normalizedSelectedIcon = normalizeIcon(iconSelected);

  function RenderSingleOption(option: StrictOption): React.ReactNode {
    const { value, label, tooltip } = option;
    const isSelected = isOptionSelected(option);
    const isDisabled = disabled || option.disabled || (disableOnMax && max && selected.length === max && !isSelected);

    const classes = classnames(
      `${baseClassName}__list__item`,
      isSelected ? `${baseClassName}__list__item--selected` : false,
      isDisabled ? `${baseClassName}__list__item--disabled` : false
    );

    const handleItemClick = () => {
      if (!isDisabled) handleChange(option);
    };
    const CustomOptionInner = components?.optionInner;
    const hasTooltip = tooltip;

    const tooltipId = `tooltip-id-${id}-${value}`;
    const content = (
      <div className={classes} onClick={handleItemClick} id={tooltipId}>
        {showIcon && (
          <div className={`${baseClassName}__list__item__icon`}>
            <WpIcon
              name={isSelected ? normalizedSelectedIcon.name : normalizedNotSelectedIcon.name}
              prefix={isSelected ? normalizedSelectedIcon.prefix : normalizedNotSelectedIcon.prefix}
            />
          </div>
        )}
        <div className={`${baseClassName}__list__item__option`}>
          {CustomOptionInner ? <CustomOptionInner option={option} /> : <>{label}</>}
        </div>
      </div>
    );

    const markup = hasTooltip ? (
      <WpTooltip placement="top">
        <WpTooltipTrigger>{content}</WpTooltipTrigger>
        <WpTooltipContent>{tooltip}</WpTooltipContent>
      </WpTooltip>
    ) : (
      content
    );

    return <React.Fragment key={`wp_list_select-${id}-${value}`}>{markup}</React.Fragment>;
  }

  const listMarkup = () => {
    return normalizedOptions.map(RenderSingleOption);
  };

  useEffect(() => {
    if (selectedOptions) {
      const normalizedSelected = selectedOptions.map(normalizeOption);
      setSelected(normalizedSelected);
    }
  }, [selectedOptions]);

  const classes = classnames(baseClassName, `${baseClassName}--${direction}`);

  return (
    <div className={classes}>
      <div className={`${baseClassName}__list`} data-test={qaTest}>
        {listMarkup()}
      </div>
    </div>
  );
};

export default WpListSelect;
